/**
 * Anwendungsgeruest: ein Hash-Router, die Ansichten, ein Datenbankhandle.
 *
 * Bewusst ohne Rahmenwerk. Die App muss in zehn Jahren noch starten; jede
 * Abhaengigkeit, die dann eine Migration verlangt, ist ein spaeterer Abend.
 *
 * Seit dem mobilen Sprint traegt das Geruest drei Dinge, die eine Webseite
 * nicht braucht und eine App nicht entbehren kann: eine Tab-Leiste am unteren
 * Rand, ein Gedaechtnis fuer die Scrollstelle und eine Richtung, in die sich
 * die Ansichten schieben. Alles drei haengt am Router, nicht an den Ansichten —
 * eine Ansicht soll nicht wissen muessen, woher man kam.
 */

import '@fontsource/ibm-plex-sans/400.css';
import '@fontsource/ibm-plex-sans/500.css';
import '@fontsource/ibm-plex-sans-condensed/600.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
// Leaflet vor der eigenen Datei: Die Ansichten laden es sonst erst spaeter, und
// dann gewinnt bei gleicher Spezifitaet Leaflets helles Grau — die dunklen
// Regeln fuer Tooltip, Popup, Zoomknoepfe und Kartengrund in style.css blieben
// wirkungslos. Ein Import an dieser Stelle stellt die Reihenfolge richtig.
import 'leaflet/dist/leaflet.css';
import './style.css';

import { starteAutomatik } from './automatik.js';
import { SPEICHER, alleAus, alleTauchgaenge, oeffne, tauchgang } from './db.js';
import { ansichtDetail } from './views/detail.js';
import { ansichtFormular } from './views/formular.js';
import { ansichtImport } from './views/import.js';
import { ansichtCheckin } from './views/checkin.js';
import { ansichtKarte } from './views/karte.js';
import { ansichtListe } from './views/liste.js';
import { ansichtStammdaten } from './views/stammdaten.js';
import { ansichtVerorten } from './views/verorten.js';
import { ansichtStatistik } from './views/statistik.js';
import { ansichtSync, nimmAnkerEntgegen } from './views/sync.js';
import { ausHash, zuHash } from './suche.js';
import { ebene, elternPfad, istFormular, pfadAus, richtung, titelFuer, zielFuer }
  from './navigation.js';
import { merke, stelleFuer } from './verlauf.js';
import { beiUmbruch, istGrob, istSchmal } from './mobil.js';
import { symbol } from './symbole.js';
import { tableiste } from './views/tableiste.js';
import { beginntZurueck, istWaagerecht, wischentscheid } from './geste.js';

const wurzel = document.querySelector('#app');
const kopf = document.querySelector('.kopf');
const kopfzeile = document.querySelector('#kopf-meta');
const kopftitel = document.querySelector('.ansicht-wort');
const zurueckKnopf = document.querySelector('.kopf-zurueck');
const kopfaktionen = document.querySelector('#kopf-aktionen');
let db = null;
let zwischenspeicher = null;   // Kopfdaten der Liste, einmal geholt
let laufendeAnsicht = null;
let aktuellerPfad = null;
// Was der naechste Ansichtswechsel tun soll — vor dem `await` gesetzt, nach dem
// `await` gebraucht. Die Ansichten holen ihre Daten asynchron; ohne diesen
// Zwischenspeicher waere beim Zeichnen nicht mehr bekannt, woher man kam.
let uebergang = { richtung: 'quer', stelle: 0 };

zurueckKnopf.append(symbol('zurueck', 22));

const leiste = tableiste();
document.body.append(leiste);

async function tauchgaenge() {
  if (!zwischenspeicher) zwischenspeicher = await alleTauchgaenge(db);
  return zwischenspeicher;
}

/** Der aktive Reiter wird hervorgehoben — sonst weiss niemand, wo er ist. */
function markiereNavigation(pfad) {
  for (const verweis of document.querySelectorAll('.kopf-nav a')) {
    const ziel = verweis.dataset.pfad;
    verweis.classList.toggle('aktiv', ziel === pfad);
  }
}

/**
 * Liste und Statistik teilen sich die Auswahl: Wer nach 2023 filtert und dann
 * auf Statistik klickt, will die Zahlen zu 2023 sehen — nicht zum ganzen
 * Bestand. Die Suchbegriffe wandern deshalb in beide Verweise.
 */
function fuehreAuswahlMit(kriterien) {
  const anhang = zuHash(kriterien).replace(/^#\//, '');
  for (const verweis of document.querySelectorAll('.kopf-nav a')) {
    const pfad = verweis.dataset.pfad;
    if (pfad === '/') verweis.href = `#/${anhang}`;
    if (pfad === '/statistik') verweis.href = `#/statistik${anhang}`;
  }
}

document.addEventListener('kriterien', (ereignis) => fuehreAuswahlMit(ereignis.detail));

/**
 * Die Kopfzeile sagt, wo man ist — auf dem Telefon ist sie die einzige Stelle,
 * die es noch sagen kann: Acht Reiter nebeneinander passen dort nicht, und die
 * Tab-Leiste kennt nur ihre vier Ziele.
 */
function zeigeKopf(pfad) {
  const eltern = elternPfad(pfad);
  const ziel = zielFuer(pfad);
  document.body.dataset.pfad = ziel.pfad;
  document.body.dataset.ebene = String(ebene(pfad));
  document.body.dataset.modal = istFormular(pfad) ? 'ja' : 'nein';
  kopftitel.textContent = titelFuer(pfad);
  zurueckKnopf.hidden = eltern === null;
  zurueckKnopf.dataset.ziel = eltern ?? '';
  leiste.markiere(pfad);
}

zurueckKnopf.addEventListener('click', () => {
  const ziel = zurueckKnopf.dataset.ziel;
  if (ziel) location.hash = `#${ziel}`;
});

/**
 * Uebergaenge nur dort, wo sie etwas erklaeren — und nie gegen den Wunsch.
 *
 * Auf dem Schreibtisch bleibt der Wechsel hart: Dort erklaert die Bewegung
 * nichts (die Reiter stehen sichtbar nebeneinander), und eine Aufnahme der
 * ganzen Seite kostet bei 995 Zeilen mehr, als sie bringt.
 */
function uebergangErlaubt() {
  if (!document.startViewTransition || !istSchmal()) return false;
  return !globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Die Hauptaktion einer Ansicht wandert in die Kopfzeile.
 *
 * Auf dem Telefon ist oben rechts der Platz für „Bearbeiten" — dort steht er in
 * jeder App, und im Fliesstext darüber kostet er eine ganze Zeile. Die Ansicht
 * markiert dazu ein Element mit `data-kopfaktion`; sie weiss nicht, wohin es
 * wandert, und die Hülle weiss nicht, was darin steht. Auf breiten Schirmen
 * bleibt alles an Ort.
 */
let geliehen = null;

/** Die geliehene Aktion zurück an ihren Platz — vor jedem Wechsel und beim
 *  Breiterwerden des Fensters. */
function gibAktionZurueck() {
  if (geliehen) geliehen.heimat?.insertBefore(geliehen.knopf, geliehen.davor);
  geliehen = null;
  kopfaktionen.replaceChildren();
}

function hefteAktionAn(element) {
  gibAktionZurueck();
  if (!istSchmal()) return;
  const aktion = element?.querySelector?.('[data-kopfaktion]');
  if (!aktion) return;
  geliehen = { knopf: aktion, heimat: aktion.parentElement, davor: aktion.nextSibling };
  kopfaktionen.append(aktion);
}

function zeige(element) {
  const tausche = () => {
    laufendeAnsicht?.aufraeumen?.();
    laufendeAnsicht = element;
    wurzel.replaceChildren(element);
    hefteAktionAn(element);
    // `wurzel.scrollTo()` lief ins Leere: Gescrollt wird das Fenster, `main`
    // ist kein Scrollbehaelter. Deshalb blieb die Seite beim Oeffnen eines
    // Tauchgangs dort stehen, wo die Liste stand.
    globalThis.scrollTo({ top: uebergang.stelle, behavior: 'instant' });
    // Und ein zweites Mal im naechsten Bild: Die Listenzeilen ausserhalb des
    // Sichtfelds werden erst gerechnet, wenn sie hineinkommen
    // (`content-visibility`). Im Augenblick des Wechsels ist die Seite deshalb
    // kuerzer, als sie sein wird — der Browser deckelt die Stelle am unteren
    // Ende, und man landete ein paar Zeilen zu weit oben.
    if (uebergang.stelle > 0) {
      requestAnimationFrame(() => {
        globalThis.scrollTo({ top: uebergang.stelle, behavior: 'instant' });
      });
    }
  };
  if (!uebergangErlaubt()) { tausche(); return; }
  document.documentElement.dataset.richtung = uebergang.richtung;
  const lauf = document.startViewTransition(tausche);
  lauf.finished.finally(() => { delete document.documentElement.dataset.richtung; });
}

/**
 * Die Zeile unter dem Namen der App: wie viel drinsteht.
 *
 * Sie las frueher den Merkposten `stand` und schrieb „kein Logbuch geladen",
 * wenn er fehlte. Den schreibt aber nur der Bundle-Import — wer seine
 * Tauchgaenge in der App erfasst oder ueber den Abgleich bekommt, hatte ihn
 * nie. Die App behauptete dann leer zu sein, waehrend die Liste darunter
 * voller Tauchgaenge stand.
 *
 * Gezaehlt wird deshalb, was wirklich da ist. Das kostet nichts: Die Liste
 * liegt fuer die Ansicht ohnehin schon im Zwischenspeicher.
 */
async function kopfAktualisieren() {
  const liste = await tauchgaenge();
  if (liste.length === 0) {
    kopfzeile.textContent = 'noch keine Tauchgänge';
    return;
  }
  const jahre = liste
    .map((t) => String(t.start_utc ?? '').slice(0, 4))
    .filter(Boolean)
    .sort();
  const profile = liste.filter((t) => t.has_profile).length;
  kopfzeile.textContent = `${liste.length} Tauchgänge · `
    + `${jahre[0] ?? '—'}–${jahre[jahre.length - 1] ?? '—'} · ${profile} Profile`;
}

async function route() {
  const pfad = pfadAus(location.hash);
  // Die Stelle gehoert zum Pfad, den man verlaesst — gemerkt wird deshalb
  // hier und nicht beim Ankommen.
  if (aktuellerPfad !== null && aktuellerPfad !== pfad) merke(aktuellerPfad, globalThis.scrollY);
  const wohin = richtung(aktuellerPfad, pfad);
  uebergang = { richtung: wohin, stelle: stelleFuer(pfad, wohin) };
  aktuellerPfad = pfad;
  zeigeKopf(pfad);

  if (pfad === '/import') {
    zeige(ansichtImport(db, async () => {
      zwischenspeicher = null;
      await kopfAktualisieren();
      location.hash = '#/';
    }));
    markiereNavigation(pfad);
    return;
  }
  if (pfad === '/sync') {
    zeige(ansichtSync(db));
    markiereNavigation(pfad);
    return;
  }
  if (pfad === '/checkin') {
    zeige(ansichtCheckin(db, await tauchgaenge()));
    markiereNavigation(pfad);
    return;
  }
  if (pfad === '/karte') {
    const plaetze = await alleAus(db, SPEICHER.sites);
    zeige(ansichtKarte(await tauchgaenge(), plaetze));
    markiereNavigation(pfad);
    return;
  }
  if (pfad === '/statistik') {
    const plaetze = await alleAus(db, SPEICHER.sites);
    const kriterien = ausHash(location.hash);
    zeige(ansichtStatistik(await tauchgaenge(), plaetze, kriterien));
    markiereNavigation(pfad);
    fuehreAuswahlMit(kriterien);
    return;
  }
  if (pfad === '/verorten') {
    zeige(await ansichtVerorten(db, async () => {
      zwischenspeicher = null;
      await kopfAktualisieren();
    }));
    markiereNavigation('/verorten');
    return;
  }

  if (pfad === '/stammdaten' || pfad.startsWith('/stammdaten/')) {
    const welches = pfad.startsWith('/stammdaten/')
      ? pfad.slice('/stammdaten/'.length) : 'sites';
    zeige(await ansichtStammdaten(db, welches, async () => {
      zwischenspeicher = null;
      await kopfAktualisieren();
    }));
    markiereNavigation('/stammdaten');
    return;
  }

  if (pfad === '/neu' || pfad.startsWith('/bearbeiten/')) {
    const kennung = pfad.startsWith('/bearbeiten/') ? pfad.slice('/bearbeiten/'.length) : null;
    const bestehend = kennung ? await tauchgang(db, kennung) : null;
    zeige(await ansichtFormular(db, bestehend, async (id) => {
      // Der Zwischenspeicher haelt die Kopfdaten der Liste. Ohne Verwerfen
      // zeigte die Liste nach dem Speichern den alten Stand und die Statistik
      // rechnete mit veralteten Zahlen.
      zwischenspeicher = null;
      await kopfAktualisieren();
      location.hash = id ? `#/tauchgang/${id}` : '#/';
    }));
    return;
  }

  const treffer = pfad.match(/^\/tauchgang\/(.+)$/);
  if (treffer) {
    const tg = await tauchgang(db, treffer[1]);
    // „Geprueft" schreibt am Tauchgang; danach stimmt der Zwischenspeicher der
    // Liste nicht mehr, und die Statistik zaehlt eine Marke zu viel.
    zeige(await ansichtDetail(db, tg, async () => {
      zwischenspeicher = null;
      await kopfAktualisieren();
    }));
    return;
  }
  // Suchbegriffe stehen in der Adresse — der Zurück-Knopf führt zur Trefferliste
  // zurück, nicht in eine leere Liste.
  const kriterien = ausHash(location.hash);
  // Auch die Liste schreibt inzwischen — über die Sammelbearbeitung. Sie
  // bekommt denselben Rückruf wie Formular, Stammdaten und Verorten: Der
  // Zwischenspeicher wird verworfen, der Kopf gerechnet. Die Ansicht frischt
  // sich danach selbst auf; ein Neuladen der Seite würde den Abgleich
  // abwürgen, der dreissig Sekunden nach der Änderung anläuft.
  zeige(ansichtListe(db, await tauchgaenge(), kriterien, async () => {
    zwischenspeicher = null;
    await kopfAktualisieren();
  }));
  markiereNavigation('/');
  fuehreAuswahlMit(kriterien);
}

/**
 * Die Kopfzeile schrumpft, sobald gescrollt wird.
 *
 * Nicht ausblenden: Eine Leiste, die beim Scrollen verschwindet und
 * zurueckspringt, macht auf einer langen Liste mehr Unruhe als Platz. Der
 * Titel bleibt stehen, die Metazeile geht — sie ist beim Blaettern ohnehin
 * nicht die Frage.
 */
function beobachteScrollen() {
  let geplant = false;
  const pruefe = () => {
    geplant = false;
    kopf.classList.toggle('gescrollt', globalThis.scrollY > 12);
  };
  globalThis.addEventListener('scroll', () => {
    if (geplant) return;
    geplant = true;
    requestAnimationFrame(pruefe);
  }, { passive: true });
}

/**
 * Wischen vom linken Rand geht zurueck.
 *
 * Das Blatt folgt dem Finger, statt erst beim Loslassen zu springen: Nur so
 * merkt man waehrend der Bewegung, dass sie greift, und kann sie abbrechen.
 * Genommen wird die Geste erst, wenn sie deutlich waagerecht ist — bis dahin
 * scrollt die Seite weiter.
 */
function wischZurueck() {
  if (!istGrob()) return;
  let startX = null;
  let startY = 0;
  let startZeit = 0;
  let genommen = false;

  const zuruecksetzen = () => {
    wurzel.classList.remove('wischt');
    wurzel.style.transform = '';
    startX = null;
    genommen = false;
  };

  document.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'mouse') return;
    if (!beginntZurueck(e.clientX, Boolean(zurueckKnopf.dataset.ziel))) return;
    startX = e.clientX;
    startY = e.clientY;
    startZeit = e.timeStamp;
  }, { passive: true });

  document.addEventListener('pointermove', (e) => {
    if (startX === null) return;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    if (!genommen) {
      if (!istWaagerecht(dx, dy)) {
        // Senkrecht losgelaufen: Das ist Scrollen, nicht Zurueckgehen.
        if (Math.abs(dy) > Math.abs(dx)) startX = null;
        return;
      }
      genommen = true;
      wurzel.classList.add('wischt');
    }
    wurzel.style.transform = `translateX(${Math.max(0, dx)}px)`;
    wurzel.style.opacity = String(Math.max(0.4, 1 - dx / (globalThis.innerWidth || 390)));
  }, { passive: true });

  const loslassen = (e) => {
    if (startX === null) return;
    const dx = e.clientX - startX;
    const zurueck = genommen && wischentscheid({
      dx, dy: e.clientY - startY, dauer: e.timeStamp - startZeit,
      breite: globalThis.innerWidth,
    });
    zuruecksetzen();
    wurzel.style.opacity = '';
    if (zurueck && zurueckKnopf.dataset.ziel) location.hash = `#${zurueckKnopf.dataset.ziel}`;
  };

  document.addEventListener('pointerup', loslassen, { passive: true });
  document.addEventListener('pointercancel', () => { zuruecksetzen(); wurzel.style.opacity = ''; });
}

async function start() {
  // Vor allem anderen: Kam gerade eine Anmeldung im Adressanker zurück?
  // Google legt das Zugriffsrecht dorthin, wo diese App ihre Pfade führt —
  // ungeprüft landete man damit in der Liste statt im Sync-Reiter.
  if (nimmAnkerEntgegen()) location.hash = '#/sync';

  db = await oeffne();
  // Der Hintergrundabgleich haengt am Geruest, nicht an einer Ansicht: Er soll
  // laufen, gleich in welchem Reiter geschrieben wurde.
  starteAutomatik(db);
  beobachteScrollen();
  wischZurueck();
  // Wer das Fenster breiter zieht, bekommt „Bearbeiten" dorthin zurueck, wo es
  // auf dem Schreibtisch steht — und umgekehrt.
  beiUmbruch(() => hefteAktionAn(laufendeAnsicht));
  await kopfAktualisieren();
  const liste = await tauchgaenge();
  if (liste.length === 0 && location.hash !== '#/import') {
    location.hash = '#/import';
    return;                       // route() laeuft ueber das hashchange-Ereignis
  }
  await route();
}

globalThis.addEventListener('hashchange', () => { route(); });
start().catch((fehler) => {
  wurzel.innerHTML = `<p class="leer">Die App konnte nicht starten: ${fehler.message}</p>`;
});
